/**
 * OnBoarding 페이지 메시지 다국어 지원 (Step1, Step2 Slogan 등)
 *
 * 사용법:
 * import { getOnBoardingMessage } from '@src/shared/constants/OnBoardingMessages';
 * {getOnBoardingMessage('STEP1_SLOGAN')}
 * {getOnBoardingMessage('STEP2_SLOGAN', { handle: 'username' })}
 * 메시지에 \n이 있으면 내부에서 <br />로 렌더링된 React 노드를 반환합니다.
 */

import React from 'react';
import type { LocalizedMessages } from '../dto/EntityRepository';
import { useLanguageStore, Language } from '../store/languageStore';

export type { Language } from '../store/languageStore';

export type OnBoardingMessageKey =
// Hamburger menu
  | 'ONBOARDING_MENU_TITLE'
// Step nav titles
  | 'STEP_TITLE_PROFILE_URL'
  | 'STEP_TITLE_EMAIL'
  | 'STEP_TITLE_ACTIVITY_TYPE'
  | 'STEP_TITLE_PROJECT_INFO'
  | 'STEP_TITLE_WORKS_IN_PROJECT'
  | 'STEP_TITLE_PREVIEW'
  | 'STEP_UPLOAD_WORKS'
  | 'STEP_WORKS_PREVIEW'
  | 'CREATE_WORK_BUTTON'

// Each Step slogans
  | 'STEP1_SLOGAN'
  | 'STEP1_SUBSLOGAN'
  | 'STEP2_SLOGAN'
  | 'STEP2_SUBSLOGAN'
  | 'ACTIVITY_TYPE_SLOGAN'
  | 'ACTIVITY_TYPE_ARTIST_DESCRIPTION'
  | 'ACTIVITY_TYPE_CURATOR_DESCRIPTION'
  | 'ACTIVITY_TYPE_RESEARCHER_DESCRIPTION'
  | 'ACTIVITY_TYPE_EDUCATOR_DESCRIPTION'
  | 'ACTIVITY_TYPE_GROUP_DESCRIPTION'
  | 'PROJECT_INFO_SLOGAN'
  | 'PROJECT_INFO_SUBSLOGAN'
  | 'UPLOAD_WORKS_SLOGAN'
  | 'UPLOAD_WORKS_EXPLAIN_MULTI_SELECT'
  | 'UPLOAD_WORKS_EXPLAIN_MULTI_SELECT_MOBILE'
  | 'UPLOAD_WORKS_EXPLAIN_DETAIL_CUT'
  | 'UPLOAD_WORKS_DROP_TEXT'
  | 'UPLOAD_WORKS_LIMIT_TEXT'
// Step validation (nav click)
  | 'VALIDATION_DATA_NOT_LOADED'
  | 'VALIDATION_URL_EMPTY'
  | 'VALIDATION_URL_INVALID'
  | 'VALIDATION_EMAIL_EMPTY'
  | 'VALIDATION_EMAIL_INVALID'
  | 'VALIDATION_NEED_CONFIRM_EMAIL'
  | 'VALIDATION_PROJECT_COVER_REQUIRED'
  | 'VALIDATION_PROJECT_TITLE_REQUIRED'
  | 'VALIDATION_WORKS_EMPTY'
  | 'VALIDATION_WORKS_TITLE_REQUIRED'
  | 'VALIDATION_WORKS_MIN'
  | 'VALIDATION_WORKS_MAX'
  ;

const onBoardingMessages: LocalizedMessages<OnBoardingMessageKey> = {
  ONBOARDING_MENU_TITLE: {
    [Language.EN]: "ON BOARDING",
    [Language.KR]: "시작하기",
  },
  STEP1_SLOGAN: {
    [Language.EN]: "Welcome,\n Choose your URL name.",
    [Language.KR]: "먼저, 이름을 정해볼까요.",
  },
  STEP1_SUBSLOGAN: {
    [Language.EN]: "This will be your unique link.",
    [Language.KR]: "해당 값은 아래의 예시처럼 URL에 들어가는 고유 값이 됩니다.",
  },
  STEP2_SLOGAN: {
    [Language.EN]: "Thanks, {handle}.\nEmail address?",
    [Language.KR]: "이제 이메일을 입력해 보아요.",
  },
  STEP2_SUBSLOGAN: {
    [Language.EN]: "For login and verification later.",
    [Language.KR]: "로그인과 인증에 사용됩니다.",
  },
  ACTIVITY_TYPE_SLOGAN: {
    [Language.EN]: "What kind of work do you do?",
    [Language.KR]: "어떤 활동을 하고 있나요?",
  },
  ACTIVITY_TYPE_ARTIST_DESCRIPTION: {
    [Language.EN]: "Creates artworks and continues an artistic practice.",
    [Language.KR]: "예술 작품을 만들고 작품 활동을 이어갑니다.",
  },
  ACTIVITY_TYPE_CURATOR_DESCRIPTION: {
    [Language.EN]: "Plans and researches exhibitions and various art programs.",
    [Language.KR]: "전시와 다양한 예술 프로그램을 기획하고 연구합니다.",
  },
  ACTIVITY_TYPE_RESEARCHER_DESCRIPTION: {
    [Language.EN]: "Conducts and documents research related to art.",
    [Language.KR]: "예술과 관련된 연구를 진행하고 기록합니다.",
  },
  ACTIVITY_TYPE_EDUCATOR_DESCRIPTION: {
    [Language.EN]: "Plans and delivers art education programs.",
    [Language.KR]: "예술과 관련된 교육 프로그램을 기획하고 실행합니다.",
  },
  ACTIVITY_TYPE_GROUP_DESCRIPTION: {
    [Language.EN]: "Brings together participants in the art world to pursue shared activities.",
    [Language.KR]: "예술계의 여러 주체가 모여 공동의 활동을 이어갑니다.",
  },
  PROJECT_INFO_SLOGAN: {
    [Language.EN]: "Tell us about this project",
    [Language.KR]: "Project를 간단히 만들어 보세요.",
  },
  PROJECT_INFO_SUBSLOGAN: {
    [Language.EN]: "Project consists of cover image, title, info, and projectelements.",
    [Language.KR]: "Project는 대표 이미지, Title, Info, Element로 구성됩니다.",
  },
  STEP_TITLE_PROFILE_URL: {
    [Language.EN]: "Profile URL",
    [Language.KR]: "프로필 URL",
  },
  STEP_TITLE_EMAIL: {
    [Language.EN]: "Email",
    [Language.KR]: "이메일",
  },
  STEP_TITLE_ACTIVITY_TYPE: {
    [Language.EN]: "Profile",
    [Language.KR]: "프로필",
  },
  STEP_TITLE_PROJECT_INFO: {
    [Language.EN]: "Project Info",
    [Language.KR]: "프로젝트 정보",
  },
  STEP_TITLE_WORKS_IN_PROJECT: {
    [Language.EN]: "Works in project",
    [Language.KR]: "프로젝트 내 작품",
  },
  STEP_TITLE_PREVIEW: {
    [Language.EN]: "Preview",
    [Language.KR]: "미리보기",
  },
  STEP_UPLOAD_WORKS: {
    [Language.EN]: "Upload Works",
    [Language.KR]: "작품 업로드",
  },
  STEP_WORKS_PREVIEW: {
    [Language.EN]: "Works Preview",
    [Language.KR]: "작품 미리보기",
  },
  CREATE_WORK_BUTTON: {
    [Language.EN]: "Create Work",
    [Language.KR]: "작품 추가하기",
  },
  UPLOAD_WORKS_SLOGAN: {
    [Language.EN]: "Add your works",
    [Language.KR]: "작품을 추가해 보세요.",
  },
  UPLOAD_WORKS_EXPLAIN_MULTI_SELECT: {
    [Language.EN]: "Multi-select or drag & drop available.",
    [Language.KR]: "다중 선택 / 드래그 & 드롭 가능합니다.",
  },
  UPLOAD_WORKS_EXPLAIN_MULTI_SELECT_MOBILE: {
    [Language.EN]: "Multi-select available.",
    [Language.KR]: "다중 선택 가능합니다.",
  },
  UPLOAD_WORKS_EXPLAIN_DETAIL_CUT: {
    [Language.EN]: "* You can add a Detail Cut later.",
    [Language.KR]: "* 로그인 후, Detail Cut 추가 가능합니다.",
  },
  UPLOAD_WORKS_DROP_TEXT: {
    [Language.EN]: "Drop image to create work",
    [Language.KR]: "이미지를 드래그하여 놓으세요",
  },
  UPLOAD_WORKS_LIMIT_TEXT: {
    [Language.EN]: "Max {maxWorks} images • JPEG, JPG, PNG only",
    [Language.KR]: "최대 {maxWorks}개 • JPEG, JPG, PNG 형식만 가능합니다",
  },
  VALIDATION_DATA_NOT_LOADED: {
    [Language.EN]: "Data not loaded.",
    [Language.KR]: "데이터를 불러오지 못했습니다.",
  },
  VALIDATION_URL_EMPTY: {
    [Language.EN]: "Please enter a URL name.",
    [Language.KR]: "URL 이름을 입력해주세요.",
  },
  VALIDATION_URL_INVALID: {
    [Language.EN]: "Please check the URL name.",
    [Language.KR]: "이름 형식을 확인해주세요.",
  },
  VALIDATION_EMAIL_EMPTY: {
    [Language.EN]: "Email is required",
    [Language.KR]: "이메일을 입력해주세요.",
  },
  VALIDATION_EMAIL_INVALID: {
    [Language.EN]: "Please check the email format.",
    [Language.KR]: "이메일 형식을 확인해주세요.",
  },
  VALIDATION_NEED_CONFIRM_EMAIL:{
    [Language.EN]: "Please check your email again.",
    [Language.KR]: "이메일을 다시 한번 확인해주세요.",
  },
  VALIDATION_PROJECT_COVER_REQUIRED: {
    [Language.EN]: "Please upload a project cover image.",
    [Language.KR]: "대표 이미지를 업로드해주세요.",
  },
  VALIDATION_PROJECT_TITLE_REQUIRED: {
    [Language.EN]: "Project title is required.",
    [Language.KR]: "제목을 입력해주세요.",
  },
  VALIDATION_WORKS_EMPTY: {
    [Language.EN]: "Please add at least one work.",
    [Language.KR]: "최소 한 개의 작품을 추가해주세요.",
  },
  VALIDATION_WORKS_TITLE_REQUIRED: {
    [Language.EN]: "All works must have a title.",
    [Language.KR]: "작품에 제목을 입력해주세요.",
  },
  VALIDATION_WORKS_MIN: {
    [Language.EN]: "Please add at least one work.",
    [Language.KR]: "최소 한 개의 작품을 추가해주세요.",
  },
  VALIDATION_WORKS_MAX: {
    [Language.EN]: "You can add up to 5 works.",
    [Language.KR]: "최대 5개까지 추가할 수 있어요.",
  },
};

/**
 * OnBoarding 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 * \n이 포함된 메시지는 <br />가 삽입된 React 노드로 반환됩니다.
 */
export function getOnBoardingMessage(
  key: OnBoardingMessageKey,
  params?: Record<string, string | number>
): string | React.ReactNode {
  const language = useLanguageStore.getState().language;
  const message = onBoardingMessages[key];

  if (!message) {
    console.warn(`OnBoarding message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  if (translatedMessage.includes('\n')) {
    const lines = translatedMessage.split('\n');
    return React.createElement(
      React.Fragment,
      null,
      ...lines.flatMap((line, i) =>
        i < lines.length - 1 ? [line, React.createElement('br', { key: `br-${i}` })] : [line]
      )
    );
  }

  return translatedMessage;
}
